import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import 'iconify-icon';
import LanguageSwitcher from './LanguageSwitcher';
import EneaflixWordmark from './EneaflixWordmark';
import { PRODUCTS } from '../data/products';
import { useGlobalWaLink } from '../contexts/SettingsContext';

const Header = () => {
  const WA_LINK = useGlobalWaLink();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [solutionsOpen, setSolutionsOpen] = useState(false);
  const [mobileSolutionsOpen, setMobileSolutionsOpen] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);
  const [mobileJoinOpen, setMobileJoinOpen] = useState(false);
  const solutionsRef = useRef(null);
  const joinRef = useRef(null);
  const location = useLocation();
  const isHome = location.pathname === '/';
  const { t } = useTranslation();

  useEffect(() => {
    const header = document.querySelector('.motion-enter');
    if (header) setTimeout(() => header.classList.add('active'), 100);
  }, []);

  // Close dropdowns on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (solutionsRef.current && !solutionsRef.current.contains(e.target)) {
        setSolutionsOpen(false);
      }
      if (joinRef.current && !joinRef.current.contains(e.target)) {
        setJoinOpen(false);
      }
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

  const homeAnchor = (anchor) => (isHome ? `#${anchor}` : `/#${anchor}`);

  const linkClass =
    'accent-link text-xs font-mono uppercase tracking-widest text-neutral-300 hover:text-accent-orange transition-colors';

  return (
    <header className="fixed top-0 left-0 w-full z-50 glass-panel motion-enter">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 h-20 flex justify-between items-center">
        <Link to="/" className="flex items-center" data-testid="header-logo">
          <img
            src="https://customer-assets.emergentagent.com/job_ie-interactive/artifacts/6k71yijb_Logomarca_Horizontal_Laranja%26Branco.png"
            alt="Instituto Eneagrama"
            className="h-12 md:h-14 w-auto"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-6" aria-label="Main navigation">
          <a href={homeAnchor('about')} className={linkClass} data-testid="nav-link-about">
            {t('nav.about')}
          </a>

          {/* SOLUÇÕES dropdown */}
          <div className="relative" ref={solutionsRef}>
            <button
              type="button"
              onClick={() => setSolutionsOpen((v) => !v)}
              className={`${linkClass} flex items-center gap-1`}
              data-testid="nav-link-solutions"
              aria-expanded={solutionsOpen}
              aria-haspopup="true"
            >
              {t('nav.solutions')}
              <iconify-icon
                icon="solar:alt-arrow-down-linear"
                className={`transition-transform text-sm ${solutionsOpen ? 'rotate-180' : ''}`}
              ></iconify-icon>
            </button>
            {solutionsOpen && (
              <div
                className="absolute top-full right-0 mt-3 w-72 bg-[#111111] border border-[#333333] rounded-xl shadow-2xl py-2 z-50"
                data-testid="nav-solutions-dropdown"
              >
                {PRODUCTS.map((p) => (
                  <Link
                    key={p.slug}
                    to={`/${p.slug}`}
                    onClick={() => setSolutionsOpen(false)}
                    className="flex items-start gap-3 px-4 py-3 hover:bg-[#1A1A1A] transition-colors"
                    data-testid={`nav-solution-${p.slug}`}
                  >
                    <div className="w-8 h-8 rounded-full bg-[#1F1F1F] border border-[#333333] flex items-center justify-center text-accent-orange flex-shrink-0 mt-0.5">
                      <iconify-icon icon={p.icon}></iconify-icon>
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-[#EAEAEA]">{p.name}</div>
                      <div className="text-xs text-neutral-500 line-clamp-1">
                        {p.short_individual}
                      </div>
                    </div>
                  </Link>
                ))}
                {/* Separator + In Company highlight */}
                <div className="my-2 border-t border-[#333333]"></div>
                <Link
                  to="/solucoes-empresariais"
                  onClick={() => setSolutionsOpen(false)}
                  data-testid="nav-solution-in-company"
                  className="flex items-center gap-3 px-4 py-3 hover:bg-[#1A1A1A] transition-colors group"
                >
                  <div className="w-8 h-8 rounded-full bg-accent-orange/15 border border-accent-orange/40 flex items-center justify-center text-accent-orange flex-shrink-0">
                    <iconify-icon icon="solar:buildings-3-linear"></iconify-icon>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-bold text-accent-orange uppercase tracking-wider">
                      Solicitar In Company
                    </div>
                    <div className="text-xs text-neutral-500 line-clamp-1">
                      Programas para sua organização
                    </div>
                  </div>
                  <iconify-icon
                    icon="solar:arrow-right-linear"
                    className="text-accent-orange group-hover:translate-x-0.5 transition-transform"
                  ></iconify-icon>
                </Link>
              </div>
            )}
          </div>

          <Link to="/agenda" className={linkClass} data-testid="nav-link-agenda">
            {t('nav.agenda')}
          </Link>
          <Link to="/franquias" className={linkClass} data-testid="nav-link-franchises">
            Franquias
          </Link>
          <Link to="/equipe" className={linkClass} data-testid="nav-link-team">
            {t('nav.team')}
          </Link>
          <Link to="/eneaflix" className={linkClass} data-testid="nav-link-eneaflix">
            <EneaflixWordmark />
          </Link>
          <Link to="/blog" className={linkClass} data-testid="nav-link-blog">
            {t('nav.blog')}
          </Link>

          {/* FAÇA PARTE dropdown */}
          <div className="relative" ref={joinRef}>
            <button
              type="button"
              onClick={() => setJoinOpen((v) => !v)}
              className={`${linkClass} flex items-center gap-1`}
              data-testid="nav-link-join"
              aria-expanded={joinOpen}
              aria-haspopup="true"
            >
              Faça Parte
              <iconify-icon
                icon="solar:alt-arrow-down-linear"
                className={`transition-transform text-sm ${joinOpen ? 'rotate-180' : ''}`}
              ></iconify-icon>
            </button>
            {joinOpen && (
              <div
                className="absolute top-full right-0 mt-3 w-72 bg-[#111111] border border-[#333333] rounded-xl shadow-2xl py-2 z-50"
                data-testid="nav-join-dropdown"
              >
                <Link
                  to="/seja-franqueado"
                  onClick={() => setJoinOpen(false)}
                  data-testid="nav-join-franchisee"
                  className="flex items-start gap-3 px-4 py-3 hover:bg-[#1A1A1A] transition-colors"
                >
                  <div className="w-8 h-8 rounded-full bg-[#1F1F1F] border border-[#333333] flex items-center justify-center text-accent-orange flex-shrink-0 mt-0.5">
                    <iconify-icon icon="solar:shop-2-linear"></iconify-icon>
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-[#EAEAEA]">Seja um Franqueado</div>
                    <div className="text-xs text-neutral-500 line-clamp-1">
                      Leve o IE para a sua região
                    </div>
                  </div>
                </Link>
                <Link
                  to="/trabalhe-conosco"
                  onClick={() => setJoinOpen(false)}
                  data-testid="nav-join-team"
                  className="flex items-start gap-3 px-4 py-3 hover:bg-[#1A1A1A] transition-colors"
                >
                  <div className="w-8 h-8 rounded-full bg-[#1F1F1F] border border-[#333333] flex items-center justify-center text-accent-orange flex-shrink-0 mt-0.5">
                    <iconify-icon icon="solar:case-round-minimalistic-linear"></iconify-icon>
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-[#EAEAEA]">Trabalhe Conosco</div>
                    <div className="text-xs text-neutral-500 line-clamp-1">
                      Carreiras no Instituto Eneagrama
                    </div>
                  </div>
                </Link>
              </div>
            )}
          </div>
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <LanguageSwitcher />
          <a
            href={WA_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="beam-btn group"
            data-testid="header-contact-cta"
          >
            <div className="beam-btn-content">
              <span>{t('nav.contact_cta')}</span>
              <iconify-icon icon="solar:phone-linear" className="text-lg"></iconify-icon>
            </div>
          </a>
        </div>

        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="lg:hidden p-2 text-[#EAEAEA]"
          data-testid="mobile-menu-toggle"
          aria-label="Menu"
        >
          <iconify-icon
            icon={isMobileMenuOpen ? 'solar:close-square-linear' : 'solar:hamburger-menu-linear'}
            className="text-2xl"
          ></iconify-icon>
        </button>
      </div>

      {isMobileMenuOpen && (
        <div className="lg:hidden glass-panel border-t border-neutral-800">
          <nav className="px-6 py-4 space-y-4" aria-label="Mobile navigation">
            <a
              href={homeAnchor('about')}
              onClick={() => setIsMobileMenuOpen(false)}
              className={linkClass + ' block'}
            >
              {t('nav.about')}
            </a>

            {/* Mobile Soluções submenu */}
            <div>
              <button
                type="button"
                onClick={() => setMobileSolutionsOpen((v) => !v)}
                className={linkClass + ' flex items-center justify-between w-full'}
              >
                <span>{t('nav.solutions')}</span>
                <iconify-icon
                  icon="solar:alt-arrow-down-linear"
                  className={`transition-transform ${mobileSolutionsOpen ? 'rotate-180' : ''}`}
                ></iconify-icon>
              </button>
              {mobileSolutionsOpen && (
                <div className="mt-2 pl-3 border-l border-neutral-800 space-y-2">
                  {PRODUCTS.map((p) => (
                    <Link
                      key={p.slug}
                      to={`/${p.slug}`}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="block text-sm text-neutral-400 hover:text-accent-orange py-1"
                    >
                      {p.name}
                    </Link>
                  ))}
                  <Link
                    to="/solucoes-empresariais"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block text-sm font-bold uppercase tracking-wider text-accent-orange py-2 mt-1 border-t border-neutral-800 pt-3"
                  >
                    Solicitar In Company →
                  </Link>
                </div>
              )}
            </div>

            <Link
              to="/agenda"
              onClick={() => setIsMobileMenuOpen(false)}
              className={linkClass + ' block'}
            >
              {t('nav.agenda')}
            </Link>
            <Link
              to="/franquias"
              onClick={() => setIsMobileMenuOpen(false)}
              className={linkClass + ' block'}
            >
              Franquias
            </Link>
            <Link
              to="/equipe"
              onClick={() => setIsMobileMenuOpen(false)}
              className={linkClass + ' block'}
            >
              {t('nav.team')}
            </Link>
            <Link
              to="/eneaflix"
              onClick={() => setIsMobileMenuOpen(false)}
              className={linkClass + ' block'}
            >
              <EneaflixWordmark />
            </Link>
            <Link
              to="/blog"
              onClick={() => setIsMobileMenuOpen(false)}
              className={linkClass + ' block'}
            >
              {t('nav.blog')}
            </Link>

            {/* Mobile FAÇA PARTE submenu */}
            <div>
              <button
                type="button"
                onClick={() => setMobileJoinOpen((v) => !v)}
                className={linkClass + ' flex items-center justify-between w-full'}
              >
                <span>Faça Parte</span>
                <iconify-icon
                  icon="solar:alt-arrow-down-linear"
                  className={`transition-transform ${mobileJoinOpen ? 'rotate-180' : ''}`}
                ></iconify-icon>
              </button>
              {mobileJoinOpen && (
                <div className="mt-2 pl-3 border-l border-neutral-800 space-y-2">
                  <Link
                    to="/seja-franqueado"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block text-sm text-neutral-400 hover:text-accent-orange py-1"
                  >
                    Seja um Franqueado
                  </Link>
                  <Link
                    to="/trabalhe-conosco"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block text-sm text-neutral-400 hover:text-accent-orange py-1"
                  >
                    Trabalhe Conosco
                  </Link>
                </div>
              )}
            </div>

            <div className="pt-2 border-t border-neutral-800">
              <LanguageSwitcher />
            </div>
            <a
              href={WA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setIsMobileMenuOpen(false)}
              className="beam-btn w-full block"
              data-testid="mobile-contact-cta"
            >
              <div className="beam-btn-content justify-center">
                <span>{t('nav.contact_cta')}</span>
                <iconify-icon icon="solar:phone-linear" className="text-lg"></iconify-icon>
              </div>
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
